// src/admin/PromoPopupManager.jsx

import React, { useEffect, useState } from "react";
import axios from "../api/axiosInstance";
import toast, { Toaster } from "react-hot-toast";
import {
  FaPlus,
  FaEdit,
  FaTrash,
  FaTimes,
  FaImage,
  FaCheckCircle,
  FaPauseCircle,
  FaExclamationTriangle,
} from "react-icons/fa";

const emptyForm = {
  title: "",
  description: "",
  disclaimer: "",
  button_text: "Register Free",
  button_link: "/register",
  start_date: "",
  end_date: "",
  is_active: true,
  priority: 0,
  image: null,
};

export default function PromoPopupManager() {
  const [popups, setPopups] = useState([]);
  const [loading, setLoading] = useState(true);

  const [showFormModal, setShowFormModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [imagePreview, setImagePreview] = useState(null);
  const [saving, setSaving] = useState(false);

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [pendingDeleteId, setPendingDeleteId] = useState(null);

  const fetchPopups = async () => {
    setLoading(true);
    try {
      const res = await axios.get("/api/popups/admin/");
      setPopups(res.data);
    } catch (err) {
      console.error("Error fetching popups:", err);
      toast.error("Failed to load popups.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPopups();
  }, []);

  // Format an ISO datetime from the API into the value <input type="datetime-local"> expects
  const toLocalInputValue = (isoString) => {
    if (!isoString) return "";
    const d = new Date(isoString);
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
      d.getHours(),
    )}:${pad(d.getMinutes())}`;
  };

  const openCreateModal = () => {
    setEditingId(null);
    setForm(emptyForm);
    setImagePreview(null);
    setShowFormModal(true);
  };

  const openEditModal = (popup) => {
    setEditingId(popup.id);
    setForm({
      title: popup.title || "",
      description: popup.description || "",
      disclaimer: popup.disclaimer || "",
      button_text: popup.button_text || "Register Free",
      button_link: popup.button_link || "/register",
      start_date: toLocalInputValue(popup.start_date),
      end_date: toLocalInputValue(popup.end_date),
      is_active: popup.is_active,
      priority: popup.priority || 0,
      image: null,
    });
    setImagePreview(popup.image_url || null);
    setShowFormModal(true);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((p) => ({ ...p, [name]: type === "checkbox" ? checked : value }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setForm((p) => ({ ...p, image: file }));
    setImagePreview(URL.createObjectURL(file));
  };

  const handleSave = async (e) => {
    e.preventDefault();

    if (!form.title.trim()) {
      toast.error("Title is required.");
      return;
    }
    if (!form.start_date || !form.end_date) {
      toast.error("Start and end date are required.");
      return;
    }
    if (new Date(form.end_date) <= new Date(form.start_date)) {
      toast.error("End date must be after start date.");
      return;
    }

    const payload = new FormData();
    Object.entries(form).forEach(([key, value]) => {
      if (key === "image") {
        if (value) payload.append("image", value);
        return;
      }
      if (key === "start_date" || key === "end_date") {
        // datetime-local gives "YYYY-MM-DDTHH:MM" — append seconds so DRF parses it cleanly
        payload.append(key, `${value}:00`);
        return;
      }
      payload.append(key, value);
    });

    setSaving(true);
    try {
      if (editingId) {
        await axios.patch(`/api/popups/admin/${editingId}/`, payload, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        toast.success("Popup updated.");
      } else {
        await axios.post("/api/popups/admin/", payload, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        toast.success("Popup created.");
      }
      setShowFormModal(false);
      fetchPopups();
    } catch (err) {
      console.error("Error saving popup:", err);
      toast.error(err.response?.data?.error || "Failed to save popup.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = (id) => {
    setPendingDeleteId(id);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    if (!pendingDeleteId) return;
    try {
      await axios.delete(`/api/popups/admin/${pendingDeleteId}/`);
      toast.success("Popup deleted.");
      fetchPopups();
    } catch (err) {
      console.error("Error deleting popup:", err);
      toast.error("Failed to delete popup.");
    } finally {
      setShowDeleteConfirm(false);
      setPendingDeleteId(null);
    }
  };

  const isLiveNow = (popup) => {
    const now = new Date();
    return (
      popup.is_active &&
      new Date(popup.start_date) <= now &&
      now <= new Date(popup.end_date)
    );
  };

  const StatusBadge = ({ popup }) => {
    if (!popup.is_active) {
      return (
        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-slate-100 text-slate-500">
          <FaPauseCircle /> Disabled
        </span>
      );
    }
    if (isLiveNow(popup)) {
      return (
        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-700">
          <FaCheckCircle className="text-green-600" /> Live now
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-amber-100 text-amber-700">
        Scheduled
      </span>
    );
  };

  return (
    <div className="min-h-screen bg-slate-50 p-3 sm:p-5 lg:p-8">
      <Toaster position="top-center" />

      <div className="max-w-6xl mx-auto space-y-5">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
          <div>
            <h2 className="text-2xl sm:text-3xl font-bold text-slate-800">
              Promo Popups
            </h2>
            <p className="text-sm text-slate-500 mt-1">
              Control the registration banner popup shown to visitors.
            </p>
          </div>
          <button
            onClick={openCreateModal}
            className="inline-flex items-center justify-center gap-2 bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white font-semibold px-5 py-2.5 rounded-lg text-sm transition-colors"
          >
            <FaPlus className="text-xs" /> New Popup
          </button>
        </div>

        {/* Loading */}
        {loading && (
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 py-12 text-center text-slate-400">
            Loading popups...
          </div>
        )}

        {/* Empty state */}
        {!loading && popups.length === 0 && (
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 py-12 text-center text-slate-400">
            No popups created yet. Click "New Popup" to add one.
          </div>
        )}

        {/* Popup cards */}
        {!loading && popups.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {popups.map((popup) => (
              <div
                key={popup.id}
                className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden flex flex-col"
              >
                {popup.image_url ? (
                  <img
                    src={popup.image_url}
                    alt={popup.title}
                    className="w-full h-36 object-cover"
                  />
                ) : (
                  <div className="w-full h-36 bg-slate-100 flex items-center justify-center text-slate-300">
                    <FaImage className="text-3xl" />
                  </div>
                )}

                <div className="p-4 flex-1 flex flex-col">
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <h3 className="font-semibold text-slate-800 leading-snug">
                      {popup.title}
                    </h3>
                    <StatusBadge popup={popup} />
                  </div>

                  {popup.description && (
                    <p className="text-sm text-slate-500 line-clamp-2 mb-3">
                      {popup.description}
                    </p>
                  )}

                  <div className="text-xs text-slate-400 space-y-0.5 mb-4">
                    <p>From: {new Date(popup.start_date).toLocaleString()}</p>
                    <p>To: {new Date(popup.end_date).toLocaleString()}</p>
                  </div>

                  <div className="mt-auto flex gap-2">
                    <button
                      onClick={() => openEditModal(popup)}
                      className="flex-1 inline-flex items-center justify-center gap-1.5 text-cyan-700 bg-cyan-50 hover:bg-cyan-100 px-3 py-2 rounded-lg text-xs font-medium transition-colors"
                    >
                      <FaEdit /> Edit
                    </button>
                    <button
                      onClick={() => handleDelete(popup.id)}
                      className="flex-1 inline-flex items-center justify-center gap-1.5 text-red-600 bg-red-50 hover:bg-red-100 px-3 py-2 rounded-lg text-xs font-medium transition-colors"
                    >
                      <FaTrash /> Delete
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Create / Edit Modal */}
      {showFormModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg my-8">
            <div className="flex justify-between items-center px-6 pt-6 mb-4">
              <h2 className="text-lg font-bold text-slate-800">
                {editingId ? "Edit Popup" : "New Popup"}
              </h2>
              <button
                onClick={() => setShowFormModal(false)}
                className="text-slate-400 hover:text-slate-600"
              >
                <FaTimes />
              </button>
            </div>

            <form
              onSubmit={handleSave}
              className="px-6 pb-6 space-y-4 max-h-[75vh] overflow-y-auto"
            >
              {/* Image upload */}
              <div>
                <label className="block mb-1.5 text-xs font-medium text-slate-600">
                  Banner Image
                </label>
                <div className="flex items-center gap-3">
                  <div className="w-20 h-20 rounded-lg bg-slate-100 flex items-center justify-center overflow-hidden flex-shrink-0">
                    {imagePreview ? (
                      <img
                        src={imagePreview}
                        alt="Preview"
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <FaImage className="text-slate-300 text-xl" />
                    )}
                  </div>
                  <label className="flex-1 cursor-pointer text-center text-sm border-2 border-dashed border-slate-300 rounded-lg py-2.5 text-slate-500 hover:border-cyan-400 hover:text-cyan-600 transition-colors">
                    Choose image
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageChange}
                      className="hidden"
                    />
                  </label>
                </div>
              </div>

              {/* Title */}
              <div>
                <label className="block mb-1.5 text-xs font-medium text-slate-600">
                  Title <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="title"
                  value={form.title}
                  onChange={handleChange}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500"
                  placeholder="e.g. Free Registration for All!"
                  required
                />
              </div>

              {/* Description */}
              <div>
                <label className="block mb-1.5 text-xs font-medium text-slate-600">
                  Description
                </label>
                <textarea
                  name="description"
                  value={form.description}
                  onChange={handleChange}
                  rows={3}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500 resize-none"
                  placeholder="Short message shown below the title"
                />
              </div>

              {/* CTA button text + link */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block mb-1.5 text-xs font-medium text-slate-600">
                    Button Text
                  </label>
                  <input
                    type="text"
                    name="button_text"
                    value={form.button_text}
                    onChange={handleChange}
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500"
                  />
                </div>
                <div>
                  <label className="block mb-1.5 text-xs font-medium text-slate-600">
                    Button Link
                  </label>
                  <input
                    type="text"
                    name="button_link"
                    value={form.button_link}
                    onChange={handleChange}
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500"
                    placeholder="/register or https://..."
                  />
                </div>
              </div>

              {/* Date range */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block mb-1.5 text-xs font-medium text-slate-600">
                    From Date <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="datetime-local"
                    name="start_date"
                    value={form.start_date}
                    onChange={handleChange}
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500"
                    required
                  />
                </div>
                <div>
                  <label className="block mb-1.5 text-xs font-medium text-slate-600">
                    To Date <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="datetime-local"
                    name="end_date"
                    value={form.end_date}
                    onChange={handleChange}
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500"
                    required
                  />
                </div>
              </div>

              {/* Disclaimer */}
              <div>
                <label className="block mb-1.5 text-xs font-medium text-slate-600">
                  Disclaimer (small print)
                </label>
                <input
                  type="text"
                  name="disclaimer"
                  value={form.disclaimer}
                  onChange={handleChange}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500"
                  placeholder="e.g. Terms and conditions apply."
                />
              </div>

              {/* Priority + Active toggle */}
              <div className="flex items-center justify-between gap-4 pt-1">
                <div className="flex-1">
                  <label className="block mb-1.5 text-xs font-medium text-slate-600">
                    Priority
                  </label>
                  <input
                    type="number"
                    name="priority"
                    value={form.priority}
                    onChange={handleChange}
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500"
                  />
                </div>

                <label className="flex items-center gap-2 cursor-pointer pt-5">
                  <input
                    type="checkbox"
                    name="is_active"
                    checked={form.is_active}
                    onChange={handleChange}
                    className="w-4 h-4 accent-cyan-600"
                  />
                  <span className="text-sm font-medium text-slate-600">
                    Active
                  </span>
                </label>
              </div>

              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowFormModal(false)}
                  className="px-4 py-2 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-600 text-sm font-medium transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-4 py-2 rounded-lg bg-cyan-600 hover:bg-cyan-700 disabled:opacity-50 text-white text-sm font-medium transition-colors"
                >
                  {saving
                    ? "Saving..."
                    : editingId
                      ? "Save Changes"
                      : "Create Popup"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete confirmation */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6 text-center">
            <div className="mx-auto w-12 h-12 rounded-full bg-red-100 flex items-center justify-center mb-4">
              <FaExclamationTriangle className="text-red-500 text-xl" />
            </div>
            <h3 className="text-lg font-bold text-slate-800 mb-1">
              Delete this popup?
            </h3>
            <p className="text-sm text-slate-500 mb-6">
              This action cannot be undone.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setPendingDeleteId(null);
                }}
                className="flex-1 px-4 py-2 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-600 text-sm font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                className="flex-1 px-4 py-2 rounded-lg bg-red-600 hover:bg-red-700 text-white text-sm font-medium transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
